import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Download, Linkedin, MessageCircle, Copy, Share2, Sparkles, Loader2, Globe, Lock } from "lucide-react";

/**
 * Shareable snapshot of a user's "My Circle" mind map.
 * Captures a target DOM node as PNG, lets the user download or share it.
 */
const CircleSnapshot = ({ open, onOpenChange, targetRef, userName = "My" }) => {
  const [generating, setGenerating] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const [publicUrl, setPublicUrl] = useState(null);
  const [visibility, setVisibility] = useState("public"); // "public" | "private"
  const [savingVisibility, setSavingVisibility] = useState(false);
  const canvasContainerRef = useRef(null);

  // Fetch the user's public circle slug + visibility on open
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const run = async () => {
      try {
        const res = await fetch("/api/network/my-slug", { credentials: "include" });
        if (!res.ok) return;
        const d = await res.json();
        if (cancelled) return;
        if (d?.slug) {
          setPublicUrl(`${window.location.origin}/api/og/c/${d.slug}`);
        }
        if (d?.visibility) setVisibility(d.visibility);
      } catch {}
    };
    run();
    return () => { cancelled = true; };
  }, [open]);

  const toggleVisibility = async () => {
    const next = visibility === "public" ? "private" : "public";
    setSavingVisibility(true);
    try {
      const res = await fetch("/api/network/circle-visibility", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ visibility: next }),
      });
      if (!res.ok) throw new Error();
      setVisibility(next);
      toast.success(next === "public" ? "Circle is public" : "Circle is now private");
    } catch {
      toast.error("Couldn't update privacy");
    } finally {
      setSavingVisibility(false);
    }
  };

  const capture = async () => {
    if (!targetRef?.current) {
      toast.error("Nothing to capture yet");
      return;
    }
    setGenerating(true);
    // Rewrite external avatar img srcs → proxy for CORS
    const PROXY_HOSTS = [
      "https://i.pravatar.cc/",
      "https://lh3.googleusercontent.com/",
      "https://lh4.googleusercontent.com/",
      "https://lh5.googleusercontent.com/",
      "https://lh6.googleusercontent.com/",
      "https://graph.facebook.com/",
      "https://platform-lookaside.fbsbx.com/",
    ];
    const imgs = Array.from(targetRef.current.querySelectorAll("img"));
    const originals = imgs.map((img) => ({ el: img, src: img.src }));
    const rewrites = imgs
      .filter((img) => PROXY_HOSTS.some((h) => img.src.startsWith(h)))
      .map((img) => {
        const proxied = `/api/network/image-proxy?url=${encodeURIComponent(img.src)}`;
        return new Promise((resolve) => {
          const preload = new Image();
          preload.crossOrigin = "anonymous";
          preload.onload = () => {
            img.crossOrigin = "anonymous";
            img.src = proxied;
            // Wait one frame for DOM update
            requestAnimationFrame(() => resolve());
          };
          preload.onerror = () => resolve(); // give up on this image
          preload.src = proxied;
        });
      });
    try {
      await Promise.all(rewrites);
      // Brief delay so swapped images fully render
      await new Promise((r) => setTimeout(r, 200));
      const dataUrl = await toPng(targetRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#f1f0fb",
        skipFonts: true,
        filter: (node) => !node.classList?.contains("mindmap-tooltip"),
      });
      setImageUrl(dataUrl);
    } catch (err) {
      console.error(err);
      toast.error("Couldn't generate snapshot. Try again?");
    } finally {
      // Restore original srcs (so live view isn't affected)
      originals.forEach(({ el, src }) => { el.src = src; el.removeAttribute("crossorigin"); });
      setGenerating(false);
    }
  };

  // Auto-capture when dialog opens; reset when closed
  useEffect(() => {
    if (open && !imageUrl && !generating) {
      const t = setTimeout(capture, 200);
      return () => clearTimeout(t);
    }
    if (!open && imageUrl) {
      setImageUrl(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleOpenChange = (isOpen) => {
    onOpenChange(isOpen);
  };

  const download = () => {
    if (!imageUrl) return;
    const link = document.createElement("a");
    const safeName = (userName || "my")
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "") // strip diacritics: Martín → Martin
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
    link.download = `${safeName || "my"}-circle-evoluras.png`;
    link.href = imageUrl;
    link.click();
    toast.success("Snapshot downloaded");
  };

  const shareUrl = publicUrl || (typeof window !== "undefined" ? `${window.location.origin}/welcome` : "");
  const shareText = publicUrl
    ? `This is my growth circle on Evoluras — the people whose honest take shapes my journey. Take a look: ${publicUrl}`
    : `This is my growth circle on Evoluras — the people whose honest take shapes my journey. Build yours: ${shareUrl}`;

  const shareLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      "_blank",
      "width=600,height=500"
    );
  };

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank");
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      toast.success("Copied — now paste anywhere");
    } catch {
      toast.error("Couldn't copy");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg" data-testid="circle-snapshot-dialog">
        <DialogHeader>
          <DialogTitle
            className="flex items-center gap-2"
            style={{ fontFamily: "system-ui, sans-serif" }}
          >
            <Share2 className="w-5 h-5 text-violet-600" />
            Share your circle
          </DialogTitle>
          <DialogDescription>
            Download the snapshot or share it with people you&apos;d love in your circle.
          </DialogDescription>
        </DialogHeader>

        {/* Preview */}
        <div
          ref={canvasContainerRef}
          className="relative rounded-xl overflow-hidden border border-slate-200 bg-slate-50"
          style={{ aspectRatio: "16/10", minHeight: 220 }}
          data-testid="snapshot-preview"
        >
          {generating && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 z-10">
              <Loader2 className="w-6 h-6 text-violet-600 animate-spin" />
              <p className="text-xs text-slate-500 mt-2">Capturing your circle...</p>
            </div>
          )}
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Your circle"
              className="w-full h-full object-contain"
              data-testid="snapshot-image"
            />
          ) : !generating ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <Button onClick={capture} variant="outline" className="rounded-full">
                <Sparkles className="w-4 h-4 mr-2" />
                Generate snapshot
              </Button>
            </div>
          ) : null}
        </div>

        {/* Primary action — download */}
        <Button
          onClick={download}
          disabled={!imageUrl}
          className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full h-11 shadow-md shadow-violet-500/20"
          data-testid="snapshot-download-btn"
        >
          <Download className="w-4 h-4 mr-2" />
          Download PNG
        </Button>

        {/* Divider */}
        <div className="flex items-center gap-3 text-xs text-slate-400 my-1">
          <div className="flex-1 h-px bg-slate-200" />
          or share directly
          <div className="flex-1 h-px bg-slate-200" />
        </div>

        {/* Privacy toggle */}
        <div
          className="flex items-center justify-between gap-3 p-2.5 rounded-lg border border-slate-200 bg-slate-50/60"
          data-testid="visibility-toggle-row"
        >
          <div className="flex items-center gap-2 min-w-0">
            {visibility === "public" ? (
              <Globe className="w-4 h-4 text-emerald-600 flex-shrink-0" />
            ) : (
              <Lock className="w-4 h-4 text-slate-500 flex-shrink-0" />
            )}
            <div className="min-w-0">
              <p className="text-xs font-semibold text-slate-800">
                {visibility === "public" ? "Public link enabled" : "Private"}
              </p>
              <p className="text-[11px] text-slate-500 leading-tight">
                {visibility === "public"
                  ? "Anyone with the link can view your circle"
                  : "Only you can see your circle — public link returns 404"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={toggleVisibility}
            disabled={savingVisibility}
            className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${
              visibility === "public" ? "bg-emerald-500" : "bg-slate-300"
            } ${savingVisibility ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
            data-testid="visibility-toggle"
            aria-label={`Toggle circle privacy (currently ${visibility})`}
          >
            <span
              className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${
                visibility === "public" ? "translate-x-5" : "translate-x-0.5"
              }`}
            />
          </button>
        </div>

        {/* Public link (if available AND public) */}
        {publicUrl && visibility === "public" && (
          <div className="flex items-center gap-2 p-2.5 bg-violet-50/60 rounded-lg border border-violet-100" data-testid="public-link-row">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-violet-700 uppercase tracking-wide">Your public circle</p>
              <p className="text-xs text-slate-700 truncate">{publicUrl}</p>
            </div>
            <Button
              size="sm"
              variant="ghost"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(publicUrl);
                  toast.success("Public link copied");
                } catch {
                  toast.error("Couldn't copy");
                }
              }}
              className="flex-shrink-0 text-violet-600 hover:bg-violet-100"
              data-testid="snapshot-copy-public-url"
            >
              <Copy className="w-3.5 h-3.5 mr-1" />
              Copy
            </Button>
          </div>
        )}

        {/* Share options — social links disabled when private */}
        <div className="grid grid-cols-3 gap-2">
          <button
            onClick={shareLinkedIn}
            disabled={visibility !== "public"}
            className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 hover:border-violet-300 transition-all hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:bg-transparent disabled:hover:border-slate-200"
            data-testid="snapshot-share-linkedin"
          >
            <Linkedin className="w-5 h-5 text-[#0A66C2]" />
            <span className="text-xs font-medium text-slate-700">LinkedIn</span>
          </button>
          <button
            onClick={shareWhatsApp}
            disabled={visibility !== "public"}
            className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 hover:border-green-300 transition-all hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:bg-transparent disabled:hover:border-slate-200"
            data-testid="snapshot-share-whatsapp"
          >
            <MessageCircle className="w-5 h-5 text-[#25D366]" />
            <span className="text-xs font-medium text-slate-700">WhatsApp</span>
          </button>
          <button
            onClick={copyText}
            disabled={visibility !== "public"}
            className="flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 hover:bg-slate-50 transition-all hover:-translate-y-0.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:bg-transparent"
            data-testid="snapshot-copy-text"
          >
            <Copy className="w-5 h-5 text-slate-500" />
            <span className="text-xs font-medium text-slate-700">Copy</span>
          </button>
        </div>

        <p className="text-[11px] text-slate-400 text-center leading-relaxed">
          {visibility === "public"
            ? "Tip: download, then post on LinkedIn with your caption — people love a visual story."
            : "Set your circle to public to share via link. You can always download the PNG."}
        </p>
      </DialogContent>
    </Dialog>
  );
};

export default CircleSnapshot;
